import { createSlice, freeze, PayloadAction } from '@reduxjs/toolkit';
import {
  IAddedFactDraftData,
  IDataModelerDraftEdits,
  IDataModelerSchemaState,
  IDataModelerSlice,
  IDerivedColumnDraft,
  IDerivedFieldDefinitionDraft,
  IEditJoinDraftData,
  IJoinDraftData,
  ITemporalTableColumnsDraftData,
} from './types';
import {
  FieldClass,
  IDataModelAddDerivedFieldInput,
  IDataModelAddFieldInput,
  IDataModelEditColumnDefinitionInput,
  IDataModelFieldTypeInput,
  IDataModelUpdateFieldInput,
  IDataRefreshConfigInput,
  IDataRefreshConfigMetaFieldsFragment,
  IDataSourceInput,
  IEndDateConfigInput,
  IFieldTypeParamsFieldsFragment,
  IFunnelPartitionInput,
  ISchemaDataModelFieldsFragment,
  ISchemaDsConfigFieldsFragment,
  ISchemaFieldMetaFieldsFragment,
  ITableColumnFieldsFragment,
  WeekStart,
} from '../../../utils/apollo/graphqlSchema';
import {
  addFieldDataFromExtendFieldData,
  dataSourceMetaToInput,
  defaultAddFieldInput,
  defaultExtendFieldInput,
  emptyDataSourceInput,
  emptyEndDateInput,
  endDateMetaToInput,
  extendFieldDataFromAddFieldData,
  fieldIsDirty,
  findExtendingFieldKeyAndClass,
  generateDummySchemaObject,
  generateUniqueDataModelName,
  getAllFieldNamesKeysAndColumns,
  isModifiedDerivedDefinition,
  joinConditionIsDirty,
  updateTableKeyPrefix,
} from './utils';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { isEqual } from 'lodash';
import produce from 'immer';
import { getAllModelFields } from '../../../pages/DataModeler/DataModelerSchema/utils';

const emptyDraftEdits: (isNewSchema?: boolean) => IDataModelerDraftEdits = (
  isNewSchema?: boolean,
) => {
  return {
    isNew: isNewSchema ?? false,
    disabled: null,
    dataSource: null,
    endDate: null,
    editTablesData: [],
    editJoinsData: [],
    addedFactsData: [],
    addedDimensionsData: [],
    draftFieldsData: [],
    draftDerivedFields: [],
    updatedFieldsInputs: [],
    editColumnInputs: [],
    addedSubjects: [],
    nameError: null,
    updatedName: null,
    sampling: null,
    addFunnelPartitions: [],
    dataRefresh: null,
    description: null,
  };
};

export const defaultEmptyDraftEdits = freeze(emptyDraftEdits(), true);

export const initialSchemaState: () => IDataModelerSchemaState = () => {
  return {
    schemaDataModel: null,
    draftEdits: emptyDraftEdits(),
    uiState: {
      expandedTableKey: null,
      newDerivedColumns: {},
      editDerivedColumns: {},
      draftDerivedField: null,
    },
  };
};

const initialState: () => IDataModelerSlice = () => {
  return {
    updateInProgress: false,
    lockedTtlSeconds: 0,
    currentSchemaId: null,
    schemaStates: {},
  };
};

const emptyUpdateFieldInput: IDataModelUpdateFieldInput = freeze({
  deprecated: null,
  description: null,
  filter: null,
  groupby: null,
  key: '',
  name: null,
  derivedDefinition: null,
});

export const emptyJoinDraftData = (): IJoinDraftData => {
  return {
    conditions: [],
    temporalCondition: null,
    sqlCondition: null,
  };
};

export const emptyTemporalsDraftData = (): ITemporalTableColumnsDraftData => {
  return {
    ts: {
      column: null,
      isAutoGenerated: false,
    },
    hourOfDay: {
      column: null,
      isAutoGenerated: true,
    },
    date: {
      column: null,
      isAutoGenerated: true,
    },
    dayOfWeek: {
      column: null,
      isAutoGenerated: true,
    },
    fourWeek: {
      column: null,
      isAutoGenerated: true,
    },
    month: {
      column: null,
      isAutoGenerated: true,
    },
    quarter: {
      column: null,
      isAutoGenerated: true,
    },
    week: {
      column: null,
      isAutoGenerated: true,
    },
    year: {
      column: null,
      isAutoGenerated: true,
    },
  };
};

const findCurrentDraftIndex = (
  viewKey: string,
  tableKey: string,
  columnName: string,
  draftEdits: IDataModelerDraftEdits,
): number => {
  return draftEdits.draftFieldsData.findIndex(
    (fieldDraft) =>
      fieldDraft.viewKey === viewKey &&
      fieldDraft.tableKey === tableKey &&
      fieldDraft.column.column === columnName,
  );
};
// XXX this edits draftEdits in-place and relies on Immer to work
const removeDraftColumFromSelections = (
  viewKey: string,
  tableKey: string,
  columnName: string,
  draftEdits: IDataModelerDraftEdits,
) => {
  const columnIndex = findCurrentDraftIndex(
    viewKey,
    tableKey,
    columnName,
    draftEdits,
  );
  if (columnIndex >= 0) {
    draftEdits.draftFieldsData.splice(columnIndex, 1);
  }
};

// XXX this edits draftEdits in-place and relies on Immer to work
const removeTemporalColumnsFromSelections = (
  viewKey: string,
  tableKey: string,
  temporals: ITemporalTableColumnsDraftData,
  draftEdits: IDataModelerDraftEdits,
) => {
  if (temporals.ts.column) {
    removeDraftColumFromSelections(
      viewKey,
      tableKey,
      temporals.ts.column,
      draftEdits,
    );
  }
  if (temporals.date.column) {
    removeDraftColumFromSelections(
      viewKey,
      tableKey,
      temporals.date.column,
      draftEdits,
    );
  }
  // TODO - handle remaining props, if we expose them
};

const updateFieldClass = (
  draftEdits: IDataModelerDraftEdits,
  fieldKey: string,
  fieldClass: FieldClass,
) => {
  draftEdits.draftFieldsData.forEach((draftField) => {
    if (draftField.addInput && draftField.addInput.key === fieldKey) {
      draftField.addInput.typeInput.fieldClass = fieldClass;
      if (fieldClass === FieldClass.Subject) {
        draftField.addInput.groupby = false;
      }
    }
    if (
      draftField.extendInput &&
      draftField.extendInput.fieldKey === fieldKey
    ) {
      draftField.extendInput.typeInput.fieldClass = fieldClass;
    }
  });
};

const getTypeOverrideAndRemoveFromSelection = (
  draftEdits: IDataModelerDraftEdits,
  viewKey: string,
  tableKey: string,
  column: string,
): IDataModelFieldTypeInput | null => {
  const previousDraft = draftEdits.draftFieldsData.find(
    (draftField) =>
      draftField.tableKey === tableKey && draftField.column.column === column,
  );
  const typeInputOverride =
    previousDraft?.addInput?.typeInput ??
    previousDraft?.extendInput?.typeInput ??
    null;

  if (previousDraft) {
    removeDraftColumFromSelections(viewKey, tableKey, column, draftEdits);
  }
  return typeInputOverride;
};

// TODO - the alternative to this is to prevent tableKey change once there is at least one table added
const updateDraftEditsTableKey = (
  draftEdits: IDataModelerDraftEdits,
  oldKey: string,
  newKey: string,
) => {
  draftEdits.addedFactsData.forEach((addedFact) => {
    addedFact.tableKey = updateTableKeyPrefix(
      addedFact.tableKey,
      oldKey,
      newKey,
    );
  });
  draftEdits.addedDimensionsData.forEach((addedDim) => {
    addedDim.tableKey = updateTableKeyPrefix(addedDim.tableKey, oldKey, newKey);
  });
  draftEdits.draftFieldsData.forEach((draftField) => {
    draftField.tableKey = updateTableKeyPrefix(
      draftField.tableKey,
      oldKey,
      newKey,
    );
    if (draftField.addInput) {
      draftField.addInput.tableKey = updateTableKeyPrefix(
        draftField.tableKey,
        oldKey,
        newKey,
      );
    }
    if (draftField.extendInput) {
      draftField.extendInput.tableKey = updateTableKeyPrefix(
        draftField.tableKey,
        oldKey,
        newKey,
      );
    }
  });
};

const getCurrentSchemaState = (state: IDataModelerSlice) => {
  if (!state.currentSchemaId) {
    return null;
  }
  const schemaId = state.currentSchemaId;
  if (!state.schemaStates[schemaId]) {
    state.schemaStates[schemaId] = initialSchemaState();
  }
  return state.schemaStates[schemaId];
};

export default createSlice({
  name: 'dataModeler',
  initialState,
  reducers: {
    dataModelerDiscardCurrentEdits: (state) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (current.draftEdits.isNew) {
        delete state.schemaStates[state.currentSchemaId!!];
        state.currentSchemaId = null;
      } else {
        // reset the draft state
        current.draftEdits = emptyDraftEdits();
        current.uiState = {
          expandedTableKey: null,
          newDerivedColumns: {},
          editDerivedColumns: {},
          draftDerivedField: null,
        };
      }
    },
    dataModelerToggleExpandTable: (
      state,
      action: PayloadAction<{ tableKey: string | null; expand?: boolean }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey, expand } = action.payload;
      if (
        (tableKey === null || tableKey === current.uiState.expandedTableKey) &&
        !expand
      ) {
        current.uiState.expandedTableKey = null;
      } else {
        current.uiState.expandedTableKey = tableKey;
      }
    },
    dataModelerSetCurrentSchemaId: (
      state,
      action: PayloadAction<{ schemaId: string; reset?: boolean }>,
    ) => {
      const { schemaId, reset } = action.payload;
      if (reset || !state.schemaStates[schemaId]) {
        state.schemaStates[schemaId] = initialSchemaState();
      }
      state.currentSchemaId = schemaId;
    },
    dataModelerSetSchemaDataModel: (
      state,
      action: PayloadAction<{
        newModel: ISchemaDataModelFieldsFragment;
        setAsCurrent: boolean;
      }>,
    ) => {
      const { newModel, setAsCurrent } = action.payload;
      if (!state.schemaStates[newModel.key]) {
        state.schemaStates[newModel.key] = {
          ...initialSchemaState(),
          schemaDataModel: newModel,
        };
      } else {
        state.schemaStates[newModel.key]!.schemaDataModel = newModel;
      }
      if (setAsCurrent) {
        state.currentSchemaId = newModel.key;
      }
    },
    dataModelerCreateDraftSchema: (
      state,
      action: PayloadAction<{
        schemaKey: string;
        schemaName: string;
        templateDataSource: ISchemaDsConfigFieldsFragment | undefined;
        templateDataRefresh: IDataRefreshConfigMetaFieldsFragment | null;
      }>,
    ) => {
      const { schemaKey, schemaName, templateDataSource, templateDataRefresh } =
        action.payload;
      if (state.schemaStates[schemaKey]) {
        throw new Error('Duplicate schema key');
      }
      const dataSource = templateDataSource
        ? dataSourceMetaToInput(templateDataSource)
        : emptyDataSourceInput();
      const endDate = templateDataSource?.endDate
        ? endDateMetaToInput(templateDataSource.endDate)
        : emptyEndDateInput();

      // XXX this is only need to "pass on" timezone and weekStart
      const current = getCurrentSchemaState(state);
      const newModel = generateDummySchemaObject({
        schemaKey,
        schemaName,
        dataSource,
        endDate,
        timezone: current?.schemaDataModel?.dsConfig?.timezone ?? 'UTC',
        weekStart:
          current?.schemaDataModel?.dsConfig?.weekStart ?? WeekStart.Monday,
        dataRefresh: templateDataRefresh,
      });
      state.schemaStates[newModel.key] = {
        ...initialSchemaState(),
        schemaDataModel: newModel,
      };
      const draftEdits = state.schemaStates[newModel.key]!!.draftEdits;
      draftEdits.isNew = true;
      draftEdits.dataSource = dataSource;
      state.currentSchemaId = newModel.key;
    },
    dataModelerUpdateCurrentSchemaName: (
      state,
      action: PayloadAction<{
        newName: string;
        newKey: string | null;
        nameError: string | null;
      }>,
    ) => {
      const { newName, newKey, nameError } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (!current.schemaDataModel) {
        return;
      }
      current.draftEdits.nameError = nameError;
      if (current.draftEdits.isNew) {
        current.schemaDataModel.name = newName;
        if (newKey && newKey !== current.schemaDataModel.key) {
          // XXX now this is tricky - changing the schema key means moving the schema state under the new key
          const oldSchemaKey = state.currentSchemaId!!;
          assertCondition(current.schemaDataModel.key === oldSchemaKey);
          current.schemaDataModel.key = newKey;
          // XXX also update any tableKey prefixes
          updateDraftEditsTableKey(current.draftEdits, oldSchemaKey, newKey);
          state.currentSchemaId = newKey;
          state.schemaStates[newKey] = current;
          delete state.schemaStates[oldSchemaKey];
        }
      } else {
        if (current.schemaDataModel?.name === newName) {
          // no longer "dirty"
          current.draftEdits.updatedName = null;
        } else {
          current.draftEdits.updatedName = newName;
        }
      }
    },
    dataModelerUpdateCurrentSchemaDescription: (
      state,
      action: PayloadAction<{ newDescription: string }>,
    ) => {
      const { newDescription } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (!current.schemaDataModel) {
        return;
      }
      if (current.draftEdits.isNew) {
        current.draftEdits.description = newDescription;
      } else {
        if ((current.schemaDataModel.description ?? '') === newDescription) {
          // no longer "dirty"
          current.draftEdits.description = null;
        } else {
          current.draftEdits.description = newDescription;
        }
      }
    },
    dataModelerSetSchemaDisabled: (
      state,
      action: PayloadAction<{ disabled: boolean }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current || !current.schemaDataModel) {
        return;
      }

      const { disabled } = action.payload;
      if (disabled === current.schemaDataModel.disabled) {
        current.draftEdits.disabled = null;
      } else {
        current.draftEdits.disabled = disabled;
      }
    },
    dataModelerSetSamplingConfig: (
      state,
      action: PayloadAction<{ sample: number; cohort: number }>,
    ) => {
      const { sample, cohort } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (
        current.schemaDataModel?.sampling?.default === sample &&
        current.schemaDataModel?.sampling?.cohort === cohort
      ) {
        // XXX no longer "dirty"
        current.draftEdits.sampling = null;
      } else {
        current.draftEdits.sampling = {
          default: sample,
          cohort,
        };
      }
    },
    dataModelerUpdateDataSource: (
      state,
      action: PayloadAction<IDataSourceInput | null>,
    ) => {
      const input = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (
        !current.draftEdits?.isNew &&
        isEqual(current.draftEdits.dataSource, input)
      ) {
        // no longer "dirty"
        current.draftEdits.dataSource = null;
      } else {
        // XXX Apparently Safari somehow messes up the white space of the pasted json which then breaks
        // the parsing in the Kobol BigQuery JDBC driver. And the difference is only visible in a hexdump :/
        // eslint-disable-next-line no-control-regex
        const replaceNonAsciiRegex = /[^\x00-\x7F]/g;
        current.draftEdits.dataSource = input?.connection.bigQuery
          ? produce(input, (draft) => {
              assertNotNil(draft.connection.bigQuery);
              draft.connection.bigQuery.keyPlain =
                draft.connection.bigQuery.keyPlain.replace(
                  replaceNonAsciiRegex,
                  '',
                );
            })
          : input;
      }
    },
    dataModelerUpdateEndDateConfig: (
      state,
      action: PayloadAction<IEndDateConfigInput | null>,
    ) => {
      const input = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      if (
        !current.draftEdits?.isNew &&
        isEqual(current.draftEdits.endDate, input)
      ) {
        // no longer "dirty"
        current.draftEdits.endDate = null;
      } else {
        current.draftEdits.endDate = input;
      }
    },
    dataModelerUpdateJoinData: (
      state,
      action: PayloadAction<IEditJoinDraftData>,
    ) => {
      const { viewKey, joinData } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      // check and update a new dimension join
      const newDimIndex = current.draftEdits.addedDimensionsData.findIndex(
        (addedDimData) => addedDimData.viewKey === viewKey,
      );
      if (newDimIndex >= 0) {
        current.draftEdits.addedDimensionsData[newDimIndex].joinData = {
          ...joinData,
        };
        return;
      }
      // check and update an existing dimension join
      const editedIndex = current.draftEdits.editJoinsData.findIndex(
        (editJoinData) => editJoinData.viewKey === viewKey,
      );
      const existingJoinMeta = current.schemaDataModel?.dimensions.find(
        (dimMeta) => dimMeta.key === viewKey,
      )?.join;
      if (existingJoinMeta && 'joinPairs' in existingJoinMeta) {
        const isDirty = joinConditionIsDirty(existingJoinMeta, joinData);
        if (editedIndex >= 0) {
          if (isDirty) {
            current.draftEdits.editJoinsData[editedIndex] = {
              viewKey,
              joinData,
            };
          } else {
            current.draftEdits.editJoinsData.splice(editedIndex, 1);
          }
        } else {
          if (isDirty) {
            current.draftEdits.editJoinsData.push({ viewKey, joinData });
          }
        }
      }
    },
    dataModelAddDraftFactTableData: (
      state,
      action: PayloadAction<{
        sqlTableName: string;
        tableKey: string;
        viewKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { sqlTableName, tableKey, viewKey } = action.payload;
      const hasFactInstallDate =
        current.schemaDataModel?.installTemporals?.viewKey === viewKey ||
        current.draftEdits.addedFactsData.some(
          (draftFact) => !!draftFact.installTemporals,
        );
      const addInput = {
        tableKey,
        sqlTableName,
        temporals: emptyTemporalsDraftData(),
        installTemporals: hasFactInstallDate ? emptyTemporalsDraftData() : null,
        autoMapped: false,
      } satisfies IAddedFactDraftData;
      current.draftEdits.addedFactsData.push(addInput);
    },
    dataModelSetDraftFactTableAutoMapped: (
      state,
      action: PayloadAction<{
        tableKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey } = action.payload;
      const idx = current.draftEdits.addedFactsData.findIndex(
        (factData) => factData.tableKey === tableKey,
      );
      if (idx >= 0) {
        current.draftEdits.addedFactsData[idx].autoMapped = true;
      }
    },
    dataModelerAddDraftDimensionData: (
      state,
      action: PayloadAction<{
        sqlTableName: string;
        tableKey: string;
        viewKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { sqlTableName, tableKey, viewKey } = action.payload;
      const addInput = {
        viewKey,
        tableKey,
        sqlTableName,
        joinData: emptyJoinDraftData(),
        installTemporals: null,
      };
      current.draftEdits.addedDimensionsData.push(addInput);
    },
    dataModelerHandleRemoveDraftTableData: (
      state,
      action: PayloadAction<{ tableKey: string }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey } = action.payload;
      current.draftEdits.addedDimensionsData =
        current.draftEdits.addedDimensionsData.filter(
          (dimensionData) => dimensionData.tableKey !== tableKey,
        );
      current.draftEdits.addedFactsData =
        current.draftEdits.addedFactsData.filter(
          (factData) => factData.tableKey !== tableKey,
        );
      // XXX also remove any fields added for that dimension
      current.draftEdits.draftFieldsData =
        current.draftEdits.draftFieldsData.filter(
          (draftField) => draftField.tableKey !== tableKey,
        );
    },
    dataModelerSetEventColumn: (
      state,
      action: PayloadAction<{
        tableKey: string;
        tableColumn: ITableColumnFieldsFragment;
      }>,
    ) => {
      // XXX this reducer is way too complex, yet selecting a table event is among the most special-cases
      // required operation, along with temporals handling. We must have one and only one event field,
      // and it must be extended exactly once in each fact table. This logic could be simplifed with several
      // approaches:
      // * Prevent adding a second fact table before the event field is selected and then prevent changing it -
      // i.e. it could only be extended after that. This has undesirable UX impact though.
      // * Switch the Kobol input and the draft state to use dedicated properties for the Event field and its extends.
      // Right now the event field and its extends are just some regular members of a list with a supposedly unique
      // fieldClass (FieldClass.Event), and we need to correctly maintain that state.
      // * possibly others
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      assertNotNil(current.schemaDataModel);
      const { tableKey, tableColumn } = action.payload;

      // first - remove the previous event mapping from this table, if any
      const prevEventColumnDraftIndex =
        current.draftEdits.draftFieldsData.findIndex(
          (draft) =>
            draft.tableKey === tableKey &&
            (draft.addInput?.typeInput?.fieldClass === FieldClass.Event ||
              draft.extendInput?.typeInput?.fieldClass === FieldClass.Event),
        );
      if (prevEventColumnDraftIndex >= 0) {
        const addInput =
          current.draftEdits.draftFieldsData[prevEventColumnDraftIndex]
            ?.addInput;
        if (addInput) {
          // this is a new field, convert it to basic, to preserve customisations like name and description
          addInput.typeInput.fieldClass = FieldClass.Basic;
          // also convert any draft extends of that field to basic
          current.draftEdits.draftFieldsData.forEach((draft) => {
            if (draft.extendInput?.fieldKey === addInput.key) {
              draft.extendInput.typeInput.fieldClass = FieldClass.Basic;
            }
          });
        } else {
          // This is an "Extend" draft - just remove it
          current.draftEdits.draftFieldsData.splice(
            prevEventColumnDraftIndex,
            1,
          );
        }
      }

      // now check if this column was already mapped as Basic
      const prevTableColumnIndex = current.draftEdits.draftFieldsData.findIndex(
        (draftField) =>
          draftField.tableKey === tableKey &&
          draftField.column.column === tableColumn.column,
      );
      const prev =
        prevTableColumnIndex >= 0
          ? current.draftEdits.draftFieldsData[prevTableColumnIndex]
          : undefined;
      // a new column to map, find out whether this is the first Event column and use
      // add or extend input appropriately
      const existingEventField = current.schemaDataModel.fact.fields.find(
        (field) => field.fieldClass === FieldClass.Event,
      );
      const draftEventField = current.draftEdits.draftFieldsData.find(
        (draft) => draft.addInput?.typeInput?.fieldClass === FieldClass.Event,
      );
      const eventFieldKey =
        existingEventField?.key ?? draftEventField?.addInput?.key;

      if (!!prev?.addInput) {
        const addInput = prev.addInput;
        // mapped new field - convert to an event extending field if not the first (and only) event addInput.
        if (!!eventFieldKey) {
          const newData = extendFieldDataFromAddFieldData(
            eventFieldKey,
            prev,
            addInput,
            FieldClass.Event,
          );
          current.draftEdits.draftFieldsData.splice(
            prevTableColumnIndex,
            1,
            newData,
          );
          // convert the first extendInput (if any) for the previous field key to an addInput
          const firstExtendIndex = current.draftEdits.draftFieldsData.findIndex(
            (draft) => draft.extendInput?.fieldKey === addInput.key,
          );
          if (firstExtendIndex >= 0) {
            const newAddData = addFieldDataFromExtendFieldData(
              prev,
              addInput,
              current.draftEdits.draftFieldsData[firstExtendIndex]
                .extendInput!!,
            );
            current.draftEdits.draftFieldsData.splice(
              firstExtendIndex,
              1,
              newAddData,
            );
          }
        } else {
          // this is the first event field - keep any customized definition
          addInput.typeInput.fieldClass = FieldClass.Event;
          // update this field extends to become the new event extends
          current.draftEdits.draftFieldsData.forEach((draft) => {
            if (draft.extendInput?.fieldKey === addInput.key) {
              draft.extendInput.typeInput.fieldClass = FieldClass.Event;
            }
          });
        }
      } else {
        // Thia was either an extend input or was not mapped at all
        const typeInputOverride = prev?.extendInput?.typeInput ?? null;
        if (prev?.extendInput) {
          // remove the previous mapping,
          current.draftEdits.draftFieldsData.splice(prevTableColumnIndex, 1);
        }
        if (eventFieldKey) {
          current.draftEdits.draftFieldsData.push({
            viewKey: current.schemaDataModel.fact.key,
            tableKey,
            column: tableColumn,
            addInput: null,
            extendInput: defaultExtendFieldInput(
              tableKey,
              eventFieldKey,
              tableColumn,
              typeInputOverride,
              FieldClass.Event,
            ),
          });
        } else {
          const viewKey = current.schemaDataModel.fact.key;
          // this is the first fact table
          current.draftEdits.draftFieldsData.push({
            viewKey,
            tableKey,
            column: tableColumn,
            addInput: defaultAddFieldInput(
              viewKey,
              tableKey,
              tableColumn,
              current.schemaDataModel,
              current.draftEdits.draftFieldsData,
              current.draftEdits.draftDerivedFields,
              current.draftEdits.updatedFieldsInputs,
              FieldClass.Event,
            ),
            extendInput: null,
          });
        }
      }
    },
    dataModelerSetDraftSubjectName: (
      state,
      action: PayloadAction<{
        subjectKey: string;
        subjectName: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { subjectKey, subjectName } = action.payload;
      const index =
        current.draftEdits?.addedSubjects?.findIndex(
          (subj) => subj.subjectKey === subjectKey,
        ) ?? -1;
      if (index >= 0) {
        current.draftEdits!!.addedSubjects[index].input.subjectName =
          subjectName;
      }
    },
    dataModelerSetDraftSubject: (
      state,
      action: PayloadAction<{
        subjectName: string;
        subjectKey: string | null;
        viewKey: string;
        tableKey: string;
        tableColumn: ITableColumnFieldsFragment;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { viewKey, subjectName, subjectKey, tableKey, tableColumn } =
        action.payload;
      const updateSubjectIndex = current.draftEdits.addedSubjects.findIndex(
        (subject) => subject.subjectKey === subjectKey,
      );
      if (updateSubjectIndex >= 0) {
        const previousFieldKey =
          current.draftEdits.addedSubjects[updateSubjectIndex].input.fieldKey;
        updateFieldClass(
          current.draftEdits,
          previousFieldKey,
          FieldClass.Basic,
        );
      }
      const alreadyPublished = getAllModelFields(current.schemaDataModel).find(
        (fieldMeta) => {
          return fieldMeta.tableFields.some(
            (tf) =>
              tf.tableKey === tableKey && tf.column === tableColumn.column,
          );
        },
      );
      let fieldKey = alreadyPublished?.key;
      if (alreadyPublished) {
        // we will be converting an existing field to a subject
        assertNotNil(fieldKey);
      } else {
        const newlyMapped = current.draftEdits.draftFieldsData.find(
          (draftField) =>
            draftField.tableKey === tableKey &&
            draftField.column.column === tableColumn.column,
        );
        fieldKey =
          newlyMapped?.addInput?.key ?? newlyMapped?.extendInput?.fieldKey;
        if (fieldKey) {
          // update this and any other newly added columns with that fieldKey class
          updateFieldClass(current.draftEdits, fieldKey, FieldClass.Subject);
        } else {
          const newInput = {
            viewKey,
            tableKey,
            column: tableColumn,
            addInput: defaultAddFieldInput(
              viewKey,
              tableKey,
              tableColumn,
              current.schemaDataModel,
              current.draftEdits.draftFieldsData,
              current.draftEdits.draftDerivedFields,
              current.draftEdits.updatedFieldsInputs,
              FieldClass.Subject,
            ),
            extendInput: null,
          };
          current.draftEdits.draftFieldsData.push(newInput);
          fieldKey = newInput.addInput.key;
        }
      }
      if (updateSubjectIndex >= 0) {
        current.draftEdits.addedSubjects[updateSubjectIndex].input.fieldKey =
          fieldKey;
        current.draftEdits.addedSubjects[updateSubjectIndex].input.subjectName =
          subjectName;
      } else {
        const uniqueKey = generateUniqueDataModelName(
          'kubit_new_subject',
          null,
          current?.draftEdits?.addedSubjects?.map(
            (subject) => subject.subjectKey,
          ) ?? [],
        );
        current.draftEdits.addedSubjects.push({
          input: {
            fieldKey,
            subjectName,
          },
          subjectKey: uniqueKey,
          viewKey,
        });
      }
    },
    dataModelerRemoveDraftSubject: (
      state,
      action: PayloadAction<{
        subjectKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { subjectKey } = action.payload;
      const removeIndex = current.draftEdits.addedSubjects.findIndex(
        (subjectData) => subjectData.subjectKey === subjectKey,
      );
      if (removeIndex < 0) {
        return;
      }
      const fieldKey =
        current.draftEdits.addedSubjects[removeIndex].input.fieldKey;
      updateFieldClass(current.draftEdits, fieldKey, FieldClass.Basic);
      current.draftEdits.addedSubjects.splice(removeIndex, 1);
    },
    dataModelerExtendFactSubject: (
      state,
      action: PayloadAction<{
        subjectKey: string;
        tableKey: string;
        tableColumn: ITableColumnFieldsFragment;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current || !current.schemaDataModel) {
        return;
      }
      const { subjectKey, tableKey, tableColumn } = action.payload;
      const viewKey = current.schemaDataModel.fact.key;
      const existingSubject = current.schemaDataModel.subjects.find(
        (subject) => subject.subjectKey === subjectKey,
      );
      if (existingSubject && existingSubject.viewKey !== viewKey) {
        throw new Error('Attempt to extend a Dimension subject');
      }
      const fieldKey =
        existingSubject?.fieldKey ??
        current.draftEdits.addedSubjects.find(
          (subject) => subject.subjectKey === subjectKey,
        )?.input?.fieldKey;
      if (!fieldKey) {
        throw new Error('Could not find subject to extend');
      }
      const previousExtendColumn = current.draftEdits.draftFieldsData.find(
        (draftField) =>
          draftField.tableKey === tableKey &&
          draftField.extendInput?.fieldKey === fieldKey,
      );
      if (previousExtendColumn) {
        removeDraftColumFromSelections(
          viewKey,
          tableKey,
          previousExtendColumn.column.column,
          current.draftEdits,
        );
      }
      const typeInputOverride = getTypeOverrideAndRemoveFromSelection(
        current.draftEdits,
        viewKey,
        tableKey,
        tableColumn.column,
      );
      current.draftEdits.draftFieldsData.push({
        viewKey,
        tableKey,
        column: tableColumn,
        addInput: null,
        extendInput: defaultExtendFieldInput(
          tableKey,
          fieldKey,
          tableColumn,
          typeInputOverride,
          FieldClass.Subject,
        ),
      });
    },
    dataModelerSetEventTemporalColumn: (
      state,
      action: PayloadAction<{
        tableKey: string;
        temporalField: keyof ITemporalTableColumnsDraftData;
        tableColumn: ITableColumnFieldsFragment | null;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey, temporalField, tableColumn } = action.payload;
      const draftTableIndex = current.draftEdits.addedFactsData.findIndex(
        (factData) => factData.tableKey === tableKey,
      );
      if (draftTableIndex < 0) {
        // can't edit existing tables, for now
        return;
      }
      assertNotNil(current.schemaDataModel);
      const viewKey = current.schemaDataModel.fact.key;
      const typeInputOverride = tableColumn
        ? getTypeOverrideAndRemoveFromSelection(
            current.draftEdits,
            viewKey,
            tableKey,
            tableColumn.column,
          )
        : null;
      const prevColumn = {
        ...current.draftEdits.addedFactsData[draftTableIndex].temporals[
          temporalField
        ],
      };
      current.draftEdits.addedFactsData[draftTableIndex].temporals[
        temporalField
      ] = {
        column: tableColumn?.column ?? null,
        isAutoGenerated: !tableColumn,
      };
      // remove any mappings for the previous column
      if (prevColumn.column) {
        removeDraftColumFromSelections(
          viewKey,
          tableKey,
          prevColumn.column,
          current.draftEdits,
        );
      }
      if (tableColumn) {
        // now add the field to the draft added fields or extended fields.
        if (
          current.schemaDataModel?.fact?.tableKeys.length ||
          current.draftEdits.addedFactsData.length > 1
        ) {
          // extending temporals
          current.draftEdits.draftFieldsData.push({
            viewKey,
            tableKey,
            column: tableColumn,
            addInput: null,
            extendInput: defaultExtendFieldInput(
              tableKey,
              current.schemaDataModel.eventTemporals[temporalField]!!.fieldKey,
              tableColumn,
              typeInputOverride,
              FieldClass.Temporal,
            ),
          });
        } else {
          // this is the first fact table
          const addInput = defaultAddFieldInput(
            viewKey,
            tableKey,
            tableColumn,
            current.schemaDataModel,
            current.draftEdits.draftFieldsData,
            current.draftEdits.draftDerivedFields,
            current.draftEdits.updatedFieldsInputs,
            FieldClass.Temporal,
          );
          current.draftEdits.draftFieldsData.push({
            viewKey,
            tableKey,
            column: tableColumn,
            addInput,
            extendInput: null,
          });
          // XXX we also update in-place our draft schemaObj, this allows extending
          // fields to work as with existing schema
          current.schemaDataModel.eventTemporals[temporalField] = {
            __typename: 'DataModelTemporalFieldRef',
            fieldKey: addInput.key,
            columnRefs: [
              {
                __typename: 'DataModelTemporalColumnRef',
                column: addInput.column,
                tableKey,
                isAutoGenerated: false,
              },
            ],
          };
        }
      }
    },
    dataModelerAddDraftInstallTemporals: (
      state,
      action: PayloadAction<{
        viewKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { viewKey } = action.payload;
      if (current.schemaDataModel?.fact?.key !== viewKey) {
        // dimension
        if (current.schemaDataModel?.installTemporals) {
          console.error(
            'Install temporals are already defined in schema ' +
              current.schemaDataModel?.installTemporals.viewKey,
          );
          return;
        }
        const otherDimWithInstallTemporals =
          current.draftEdits.addedDimensionsData.find(
            (draft) => !!draft.installTemporals,
          ) ??
          current.draftEdits.editTablesData.find(
            (draft) => !!draft.installTemporals,
          );
        if (otherDimWithInstallTemporals) {
          console.error(
            'Install temporal already defined in ' +
              otherDimWithInstallTemporals.viewKey,
          );
          return;
        }
        const draftDim = current.draftEdits.addedDimensionsData.find(
          (draft) => draft.viewKey === viewKey,
        );
        if (draftDim) {
          draftDim.installTemporals = emptyTemporalsDraftData();
        } else {
          const existingDim = current.schemaDataModel?.dimensions.find(
            (dim) => dim.key === viewKey,
          );
          assertNotNil(existingDim);
          const prevData = current.draftEdits.editTablesData.find(
            (editData) =>
              editData.tableKey === existingDim.tableKeys[0] &&
              editData.viewKey === viewKey,
          );
          if (prevData) {
            prevData.installTemporals = emptyTemporalsDraftData();
          } else {
            current.draftEdits.editTablesData.push({
              viewKey,
              tableKey: existingDim.tableKeys[0],
              installTemporals: emptyTemporalsDraftData(),
              sqlTableName: null,
            });
          }
        }
      } else {
        // add/update EditedTableDraftData for each existing fact table
        current.schemaDataModel?.fact?.tableKeys?.forEach((tableKey) => {
          const prevData = current.draftEdits.editTablesData.find(
            (editData) =>
              editData.tableKey === tableKey && editData.viewKey === viewKey,
          );
          if (prevData) {
            prevData.installTemporals = emptyTemporalsDraftData();
          } else {
            current.draftEdits.editTablesData.push({
              viewKey,
              tableKey,
              installTemporals: emptyTemporalsDraftData(),
              sqlTableName: null,
            });
          }
        });
        // now add install temporals for each new fact table
        current.draftEdits.addedFactsData.forEach((draft) => {
          if (!draft.installTemporals) {
            draft.installTemporals = emptyTemporalsDraftData();
          }
        });
      }
    },
    dataModelerRemoveDraftInstallTemporals: (
      state,
      action: PayloadAction<{
        viewKey: string;
        tableKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { viewKey, tableKey } = action.payload;
      // first remove from edited existing tables, this is shared between fact and dimension
      current.draftEdits.editTablesData.forEach((editDraft) => {
        if (editDraft.viewKey === viewKey && !!editDraft.installTemporals) {
          removeTemporalColumnsFromSelections(
            viewKey,
            editDraft.tableKey,
            editDraft.installTemporals,
            current.draftEdits,
          );
          editDraft.installTemporals = null;
        }
      });
      // XXX also remove the now empty edits
      current.draftEdits.editTablesData =
        current.draftEdits.editTablesData.filter(
          (draft) => !!draft.installTemporals,
        );
      if (current.schemaDataModel?.fact?.key !== viewKey) {
        // dimension
        const draftDim = current.draftEdits.addedDimensionsData.find(
          (draft) => draft.viewKey === viewKey,
        );
        if (draftDim) {
          // remove old mappings,
          if (draftDim.installTemporals) {
            removeTemporalColumnsFromSelections(
              viewKey,
              tableKey,
              draftDim.installTemporals,
              current.draftEdits,
            );
          }
          draftDim.installTemporals = null;
        }
      } else {
        const draftFact = current.draftEdits.addedFactsData.find(
          (draft) => draft.tableKey === tableKey,
        );
        if (!draftFact) {
          return;
        }
        // remove old mappings
        if (draftFact.installTemporals) {
          removeTemporalColumnsFromSelections(
            viewKey,
            tableKey,
            draftFact.installTemporals,
            current.draftEdits,
          );
        }
        draftFact.installTemporals = null;
      }
    },
    dataModelerSetInstallTemporalColumn: (
      state,
      action: PayloadAction<{
        viewKey: string;
        tableKey: string;
        temporalField: keyof ITemporalTableColumnsDraftData;
        tableColumn: ITableColumnFieldsFragment | null;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { viewKey, tableKey, temporalField, tableColumn } = action.payload;
      const isFact = viewKey === current.schemaDataModel?.fact?.key;
      const draftTemporals =
        current.draftEdits.editTablesData.find(
          (editDraft) =>
            editDraft.viewKey === viewKey && editDraft.tableKey === tableKey,
        )?.installTemporals ??
        (isFact
          ? current.draftEdits.addedFactsData.find(
              (factData) => factData.tableKey === tableKey,
            )?.installTemporals
          : current.draftEdits.addedDimensionsData.find(
              (dimData) => dimData.tableKey === tableKey,
            )?.installTemporals);
      if (!draftTemporals) {
        console.error('Could not find draft temporals to update');
        return;
      }
      const prevColumn = {
        ...draftTemporals[temporalField],
      };
      draftTemporals[temporalField] = {
        column: tableColumn?.column ?? null,
        isAutoGenerated: !tableColumn,
      };
      // remove any mappings for the previous column
      const typeInputOverride = prevColumn?.column
        ? getTypeOverrideAndRemoveFromSelection(
            current.draftEdits,
            viewKey,
            tableKey,
            prevColumn.column,
          )
        : null;
      if (!tableColumn) {
        return;
      }
      const isExtend =
        isFact &&
        (!!current.schemaDataModel?.installTemporals ||
          current.draftEdits.editTablesData.some(
            (draft) =>
              !!draft.installTemporals &&
              !!draft.installTemporals[temporalField],
          ) ||
          current.draftEdits.addedFactsData.some(
            (draft) =>
              !!draft.installTemporals &&
              !!draft.installTemporals[temporalField],
          ));
      if (isExtend) {
        assertNotNil(current.schemaDataModel);
        current.draftEdits.draftFieldsData.push({
          viewKey,
          tableKey,
          column: tableColumn,
          addInput: null,
          extendInput: defaultExtendFieldInput(
            tableKey,
            current.schemaDataModel.eventTemporals[temporalField]!!.fieldKey,
            tableColumn,
            typeInputOverride,
            FieldClass.Temporal,
          ),
        });
      } else {
        current.draftEdits.draftFieldsData.push({
          viewKey,
          tableKey,
          column: tableColumn,
          addInput: defaultAddFieldInput(
            viewKey,
            tableKey,
            tableColumn,
            current.schemaDataModel,
            current.draftEdits.draftFieldsData,
            current.draftEdits.draftDerivedFields,
            current.draftEdits.updatedFieldsInputs,
            FieldClass.Temporal,
          ),
          extendInput: null,
        });
      }
    },
    dataModelerAddDraftFieldData: (
      state,
      action: PayloadAction<{
        viewKey: string;
        tableKey: string;
        column: ITableColumnFieldsFragment;
      }>,
    ) => {
      const { viewKey, tableKey, column } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { fieldKey: extendingFieldKey, fieldClass } =
        current.schemaDataModel?.fact?.key === viewKey
          ? findExtendingFieldKeyAndClass(
              current.schemaDataModel?.fact?.key,
              tableKey,
              column,
              current.schemaDataModel.fact.fields,
              current.draftEdits.draftFieldsData,
            )
          : { fieldKey: undefined, fieldClass: FieldClass.Basic };
      if (extendingFieldKey) {
        const extendInput = {
          tableKey,
          viewKey,
          column,
          addInput: null,
          extendInput: defaultExtendFieldInput(
            tableKey,
            extendingFieldKey,
            column,
            null,
            fieldClass,
          ),
        };
        current.draftEdits.draftFieldsData.push(extendInput);
      } else {
        const newInput = {
          tableKey,
          viewKey,
          column,
          extendInput: null,
          addInput: defaultAddFieldInput(
            viewKey,
            tableKey,
            column,
            current.schemaDataModel,
            current.draftEdits.draftFieldsData,
            current.draftEdits.draftDerivedFields,
            current.draftEdits.updatedFieldsInputs,
            FieldClass.Basic,
          ),
        };
        current.draftEdits.draftFieldsData.push(newInput);
      }
    },
    dataModelerUpdateExtendFieldTypeInput: (
      state,
      action: PayloadAction<{
        tableKey: string;
        column: string;
        input: IDataModelFieldTypeInput;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current || !current.schemaDataModel) {
        return;
      }
      const { tableKey, column, input } = action.payload;
      const viewKey = current.schemaDataModel.fact.key;
      const currentDraftIndex = findCurrentDraftIndex(
        viewKey,
        tableKey,
        column,
        current.draftEdits,
      );
      if (currentDraftIndex >= 0) {
        const cur = current.draftEdits.draftFieldsData[currentDraftIndex];
        if (cur.extendInput) {
          cur.extendInput = {
            ...cur.extendInput,
            typeInput: input,
          };
        }
      }
    },
    dataModelerSwitchDraftFieldToExtendInput: (
      state,
      action: PayloadAction<{
        tableKey: string;
        column: string;
        fieldKey: string;
      }>,
    ) => {
      const { tableKey, column, fieldKey } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current || !current.schemaDataModel) {
        return;
      }
      const viewKey = current.schemaDataModel.fact.key;
      const currentDraftIndex = findCurrentDraftIndex(
        viewKey,
        tableKey,
        column,
        current.draftEdits,
      );
      if (currentDraftIndex >= 0) {
        const cur = current.draftEdits.draftFieldsData[currentDraftIndex];
        const typeInput = cur.addInput?.typeInput ?? cur.extendInput?.typeInput;
        assertNotNil(typeInput);
        current.draftEdits.draftFieldsData[currentDraftIndex] = {
          ...cur,
          addInput: null,
          extendInput: {
            column,
            fieldKey,
            tableKey,
            typeInput,
          },
        };
      }
    },
    dataModelerSwitchDraftFieldToAddInput: (
      state,
      action: PayloadAction<{
        tableKey: string;
        column: string;
        fieldName: string | null;
      }>,
    ) => {
      const { tableKey, column, fieldName } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current || !current.schemaDataModel) {
        return;
      }
      const viewKey = current.schemaDataModel.fact.key;
      const currentDraftIndex = findCurrentDraftIndex(
        viewKey,
        tableKey,
        column,
        current.draftEdits,
      );
      if (currentDraftIndex >= 0) {
        const cur = current.draftEdits.draftFieldsData[currentDraftIndex];
        const typeInput = cur.addInput?.typeInput ?? cur.extendInput?.typeInput;
        assertNotNil(typeInput);
        const addInput = defaultAddFieldInput(
          viewKey,
          tableKey,
          cur.column,
          current.schemaDataModel,
          current.draftEdits.draftFieldsData,
          current.draftEdits.draftDerivedFields,
          current.draftEdits.updatedFieldsInputs,
        );
        current.draftEdits.draftFieldsData[currentDraftIndex] = {
          ...cur,
          addInput: {
            ...addInput,
            name: fieldName ?? addInput.name,
          },
          extendInput: null,
        };
      }
    },
    dataModelerRemoveDraftFieldData: (
      state,
      action: PayloadAction<{
        viewKey: string;
        tableKey: string;
        column: string;
      }>,
    ) => {
      const { viewKey, tableKey, column } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      removeDraftColumFromSelections(
        viewKey,
        tableKey,
        column,
        current.draftEdits,
      );
    },
    dataModelerUpdateDraftFieldData: (
      state,
      action: PayloadAction<{
        viewKey: string;
        tableKey: string | null;
        column: string;
        input: Partial<IDataModelAddFieldInput>;
      }>,
    ) => {
      const { viewKey, tableKey, column, input } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const index = current.draftEdits.draftFieldsData.findIndex(
        ({ addInput }) =>
          addInput &&
          addInput.viewKey === viewKey &&
          addInput.column === column &&
          (addInput.tableKey === null || addInput.tableKey === tableKey),
      );
      if (index >= 0) {
        const edited = current.draftEdits.draftFieldsData[index];
        current.draftEdits.draftFieldsData[index] = {
          ...edited,
          addInput: {
            ...edited.addInput!!,
            ...input,
          },
        };
      }
    },
    dataModelerUpdateExistingFieldInput: (
      state,
      action: PayloadAction<{
        fieldMeta: ISchemaFieldMetaFieldsFragment;
        input: Partial<IDataModelUpdateFieldInput> & { key: string };
      }>,
    ) => {
      const { fieldMeta, input } = action.payload;
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const existingInputIndex =
        current.draftEdits.updatedFieldsInputs.findIndex(
          (ufi) => ufi.key === input.key,
        );
      const updatedInput =
        existingInputIndex >= 0
          ? {
              ...current.draftEdits.updatedFieldsInputs[existingInputIndex],
              ...input,
            }
          : {
              ...emptyUpdateFieldInput,
              ...input,
            };
      const isDirty = fieldIsDirty(fieldMeta, updatedInput);
      if (existingInputIndex >= 0) {
        if (isDirty) {
          current.draftEdits.updatedFieldsInputs[existingInputIndex] =
            updatedInput;
        } else {
          current.draftEdits.updatedFieldsInputs.splice(existingInputIndex, 1);
        }
      } else {
        if (isDirty) {
          current.draftEdits.updatedFieldsInputs.push(updatedInput);
        }
      }
    },
    dataModelerEditExistingDerivedColumnInput: (
      state,
      action: PayloadAction<{
        originalTypeParams: IFieldTypeParamsFieldsFragment | null;
        input: IDataModelEditColumnDefinitionInput;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { originalTypeParams, input } = action.payload;
      const currentIndex = current.draftEdits.editColumnInputs.findIndex(
        (editInput) =>
          editInput.column === input.column &&
          editInput.tableKey === input.tableKey &&
          editInput.viewKey &&
          input.viewKey,
      );
      const isModified = isModifiedDerivedDefinition(
        input.column,
        originalTypeParams,
        input.customDefinition,
      );
      if (currentIndex >= 0) {
        if (isModified) {
          current.draftEdits.editColumnInputs[currentIndex] = input;
        } else {
          current.draftEdits.editColumnInputs.splice(currentIndex, 1);
        }
      } else {
        if (isModified) {
          current.draftEdits.editColumnInputs.push(input);
        }
      }
    },
    dataModelerEditDraftDerivedColumnInput: (
      state,
      action: PayloadAction<{
        input: IDataModelEditColumnDefinitionInput;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { input } = action.payload;
      const typeInput =
        current.draftEdits.draftFieldsData.find(
          (draftField) =>
            draftField.column.column === input.column &&
            draftField.addInput?.viewKey === input.viewKey &&
            draftField.addInput?.tableKey === input.tableKey,
        )?.addInput?.typeInput ??
        current.draftEdits.draftFieldsData.find(
          (draftField) =>
            draftField.column.column === input.column &&
            draftField.extendInput?.tableKey === input.tableKey,
        )?.extendInput?.typeInput;
      if (typeInput?.typeParams) {
        typeInput.typeParams.customDefinition = input.customDefinition;
      }
    },
    dataModelerAddDraftDerivedField: (
      state,
      action: PayloadAction<{
        input: IDataModelAddDerivedFieldInput;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { input } = action.payload;
      // XXX make sure the key is unique
      const { fieldKeys } = getAllFieldNamesKeysAndColumns(
        current.schemaDataModel,
        current.draftEdits.draftFieldsData,
        current.draftEdits.draftDerivedFields,
        current.draftEdits.updatedFieldsInputs,
      );
      input.key = generateUniqueDataModelName(input.key, null, fieldKeys);
      current.draftEdits.draftDerivedFields.push(input);
    },
    dataModelerRemoveDraftDerivedField: (
      state,
      action: PayloadAction<{
        fieldKey: string;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { fieldKey } = action.payload;
      const index = current.draftEdits.draftDerivedFields.findIndex(
        (draft) => draft.key === fieldKey,
      );
      if (index >= 0) {
        current.draftEdits.draftDerivedFields.splice(index, 1);
      }
    },
    dataModelerUpdateDraftDerivedField: (
      state,
      action: PayloadAction<{
        fieldKey: string;
        input: Partial<IDataModelAddDerivedFieldInput>;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { fieldKey, input } = action.payload;
      const index = current.draftEdits.draftDerivedFields.findIndex(
        (draft) => draft.key === fieldKey,
      );
      if (index >= 0) {
        current.draftEdits.draftDerivedFields[index] = {
          ...current.draftEdits.draftDerivedFields[index],
          ...input,
        };
      }
    },
    dataModelerSetUiDraftDerivedField: (
      state,
      action: PayloadAction<{
        draft: IDerivedFieldDefinitionDraft | null;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { draft } = action.payload;
      current.uiState.draftDerivedField = draft;
    },
    dataModelerAddDraftFunnelPartitionField: (
      state,
      action: PayloadAction<IFunnelPartitionInput>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const input = action.payload;
      if (
        !current.draftEdits.addFunnelPartitions.some(
          (partInput) => partInput.fieldKey === input.fieldKey,
        )
      ) {
        current.draftEdits.addFunnelPartitions.push(input);
      }
    },
    dataModelerRemoveDraftFunnelPartitionField: (
      state,
      action: PayloadAction<string>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const fieldKey = action.payload;
      const idx = current.draftEdits.addFunnelPartitions.findIndex(
        (part) => part.fieldKey === fieldKey,
      );
      if (idx >= 0) {
        current.draftEdits.addFunnelPartitions.splice(idx, 1);
      }
    },
    dataModelerUpdateDraftFunnelPartitionName: (
      state,
      action: PayloadAction<{ fieldKey: string; name: string }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { fieldKey, name } = action.payload;
      const curPartition = current.draftEdits.addFunnelPartitions.find(
        (part) => part.fieldKey === fieldKey,
      );
      if (curPartition) {
        curPartition.name = name;
      }
    },
    dataModelerUpdateDataRefreshConfig: (
      state,
      action: PayloadAction<Partial<IDataRefreshConfigInput>>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const updateData = action.payload;
      const dataRefresh = current.draftEdits.dataRefresh ?? {
        minimumRefreshSeconds: null,
        lookBackSeconds: null,
        pfvStringMaxLength: null,
        pfvMaxNumOfValuesPerKey: null,
      };
      current.draftEdits.dataRefresh = {
        ...dataRefresh,
        ...updateData,
      };
    },
    dataModelerSetUpdateInProgress: (state, action: PayloadAction<boolean>) => {
      state.updateInProgress = action.payload;
    },
    dataModelerSetLockedTtlSeconds: (state, action: PayloadAction<number>) => {
      state.lockedTtlSeconds = action.payload;
    },
    dataModelerSetDerivedColumnDraft: (
      state,
      action: PayloadAction<{
        tableKey: string;
        draft: IDerivedColumnDraft | null;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey, draft } = action.payload;
      if (!draft) {
        delete current.uiState.newDerivedColumns[tableKey];
      } else {
        current.uiState.newDerivedColumns[tableKey] = draft;
      }
    },
    dataModelerSetDerivedColumnEdit: (
      state,
      action: PayloadAction<{
        tableKey: string;
        draft: IDerivedColumnDraft | null;
      }>,
    ) => {
      const current = getCurrentSchemaState(state);
      if (!current) {
        return;
      }
      const { tableKey, draft } = action.payload;
      if (!draft) {
        delete current.uiState.editDerivedColumns[tableKey];
      } else {
        current.uiState.editDerivedColumns[tableKey] = draft;
      }
    },
  },
});
